import { useEffect } from "react";
import { create } from "zustand";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  Unsubscribe,
} from "firebase/firestore";
import { firebaseDb, firebaseAuth } from "app";
import { useCurrentUser } from "app";

/**
 * Singleton store for the current staff member's active shifts.
 *
 * Why singleton?
 *   Previously this hook was called in multiple places (NotificationBell,
 *   MyAppointments, and others). Each call site spawned its own
 *   `onSnapshot` listener against the same `staffattendance` query, which
 *   bloated the Firestore peak-listener count. With a refcounted singleton
 *   we keep at most ONE active subscription regardless of how many React
 *   components consume the data.
 */
interface ActiveShiftsState {
  activeShifts: any[];
  loading: boolean;
  error: Error | null;

  // internals
  _staffId: string | null;
  _unsubscribe: Unsubscribe | null;
  _refCount: number;

  subscribe: (staffId: string | null) => void;
  unsubscribe: () => void;
}

const useActiveShiftsStore = create<ActiveShiftsState>((set, get) => ({
  activeShifts: [],
  loading: true,
  error: null,
  _staffId: null,
  _unsubscribe: null,
  _refCount: 0,

  subscribe: (staffId) => {
    if (!staffId) {
      set({ loading: false });
      return;
    }

    // Bump refcount.
    set((s) => ({ _refCount: s._refCount + 1 }));

    // Already subscribed for this staff member → just reuse.
    if (get()._unsubscribe && get()._staffId === staffId) {
      return;
    }

    // Tear down any prior subscription (e.g. role switched accounts).
    const prior = get()._unsubscribe;
    if (prior) prior();

    set({ _staffId: staffId, loading: true });

    const attendanceQuery = query(
      collection(firebaseDb, "staffattendance"),
      where("staffId", "==", staffId),
      // Handle both with and without trailing space — legacy data.
      where("status", "in", ["active", "active "]),
    );

    const unsubscribe = onSnapshot(
      attendanceQuery,
      async (snapshot) => {
        try {
          const shiftsPromises = snapshot.docs.map(async (docSnap) => {
            const shiftData = docSnap.data();
            let enrichedData: any = {
              id: docSnap.id,
              ...shiftData,
            };
            if (shiftData.appointmentId) {
              const apptDocRef = doc(firebaseDb, "appointments", shiftData.appointmentId);
              const apptDocSnap = await getDoc(apptDocRef);
              if (apptDocSnap.exists()) {
                enrichedData = {
                  ...enrichedData,
                  appointmentData: apptDocSnap.data(),
                  itemType: "appointment",
                };
              }
            }
            return enrichedData;
          });

          const shifts = await Promise.all(shiftsPromises);
          // Safety: ignore any that have already clocked out.
          const activeOnly = shifts.filter((s) => !s.clockOutTime);
          set({ activeShifts: activeOnly, loading: false, error: null });
        } catch (err) {
          console.error("Error processing active shifts:", err);
          set({ error: err as Error, loading: false });
        }
      },
      (err) => {
        console.error("Error listening to active shifts:", err);
        set({ error: err as Error, loading: false });
      },
    );

    set({ _unsubscribe: unsubscribe });
  },

  unsubscribe: () => {
    const next = Math.max(0, get()._refCount - 1);
    set({ _refCount: next });
    if (next > 0) return;
    // Last consumer gone → kill the underlying listener.
    const u = get()._unsubscribe;
    if (u) u();
    set({ _unsubscribe: null, _staffId: null });
  },
}));

export const useActiveShifts = () => {
  const { user } = useCurrentUser();
  const { activeShifts, loading, error, subscribe, unsubscribe } = useActiveShiftsStore();

  useEffect(() => {
    const staffId = user?.uid ?? firebaseAuth.currentUser?.uid ?? null;
    subscribe(staffId);
    return () => unsubscribe();
  }, [user?.uid, subscribe, unsubscribe]);

  return { activeShifts, loading, error };
};
